import React, { useEffect, useState, useRef } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import {
    getPodcasts,
    createPodcast,
    updatePodcast,
    deletePodcast,
    fetchProtected,
} from "./api";

function Podcasts({ token, onLogout, embedded = false }) {
    const [podcasts, setPodcasts] = useState([]);
    const [clients, setClients] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalError, setModalError] = useState("");
    const [saving, setSaving] = useState(false);

    const emptyForm = {
        id: null,
        client_id: "",
        name: "",
        // boolean flags:
        audio_production: false,
        show_notes: false,
        distribution: false,
        hosting: false,
        short_form_video: false,
        long_form_video: false,
        blog_post_creation: false,
        podcast_website: false,
        email_marketing: false,
        episode_development: false,
        real_time_producer: false,
        guest_outreach: false,
        paywall_content: false,
        // text fields:
        riverside: "",
        riverside_username: "",
        riverside_password: "",
        frequency: "",
        publication_day: "",
        monthly_cost: "0.00",
        notification_emails: "",
        notes: "",
        modified_raw: "",
        captivate_show_id: "",
        sharepoint_folder_name: "",
        apple_podcasts_url: "",
        spotify_podcasts_url: "",
    };

    const [form, setForm] = useState(emptyForm);

    // Snapshot of form when modal is opened (for unsaved-changes check)
    const [initialFormSnapshot, setInitialFormSnapshot] = useState(emptyForm);

    // Ref to the modal form so we can trigger submit from keyboard shortcut
    const formRef = useRef(null);

    // Helper: detect unsaved changes
    const hasUnsavedChanges =
        JSON.stringify(form) !== JSON.stringify(initialFormSnapshot);

    // map DB row -> form shape
    const mapPodcastToForm = (p) => ({
        id: p.id,
        client_id: p.client_id || "",
        name: p.name || "",
        audio_production: !!p.audio_production,
        show_notes: !!p.show_notes,
        distribution: !!p.distribution,
        hosting: !!p.hosting,
        short_form_video: !!p.short_form_video,
        long_form_video: !!p.long_form_video,
        blog_post_creation: !!p.blog_post_creation,
        podcast_website: !!p.podcast_website,
        email_marketing: !!p.email_marketing,
        episode_development: !!p.episode_development,
        real_time_producer: !!p.real_time_producer,
        guest_outreach: !!p.guest_outreach,
        paywall_content: !!p.paywall_content,
        riverside: p.riverside || "",
        riverside_username: p.riverside_username || "",
        riverside_password: p.riverside_password || "",
        frequency: p.frequency || "",
        publication_day: p.publication_day || "",
        monthly_cost: p.monthly_cost != null ? String(p.monthly_cost) : "0.00",
        notification_emails: p.notification_emails || "",
        notes: p.notes || "",
        modified_raw: p.modified_raw || "",
        captivate_show_id: p.captivate_show_id || "",
        sharepoint_folder_name: p.sharepoint_folder_name || "",
        apple_podcasts_url: p.apple_podcasts_url || "",
        spotify_podcasts_url: p.spotify_podcasts_url || "",
    });

    useEffect(() => {
        async function load() {
            setLoading(true);
            setError("");

            try {
                const p = await getPodcasts();
                if (p.ok) setPodcasts(p.podcasts);
                else setError(p.message || "Failed to load podcasts");

                const c = await fetchProtected("/clients");
                if (c?.ok) setClients(c.clients);
            } catch (err) {
                console.error(err);
                setError("Failed to load podcasts");
            } finally {
                setLoading(false);
            }
        }

        load();
    }, []);

    const resetForm = () => {
        setForm(emptyForm);
        setInitialFormSnapshot(emptyForm);
    };

    // ----- Modal helpers -----

    // Core close (no checks)
    const closeModal = () => {
        setIsModalOpen(false);
        setModalError("");
        resetForm();
    };

    // Guarded close: asks about unsaved changes
    const requestCloseModal = () => {
        if (hasUnsavedChanges) {
            const ok = window.confirm(
                "You have unsaved changes. Close without saving?"
            );
            if (!ok) return;
        }
        closeModal();
    };

    const handleOpenCreate = () => {
        const fresh = { ...emptyForm };
        setForm(fresh);
        setInitialFormSnapshot(fresh);
        setModalError("");
        setIsModalOpen(true);
    };

    const handleOpenEdit = (p) => {
        const mapped = mapPodcastToForm(p);
        setForm(mapped);
        setInitialFormSnapshot(mapped);
        setModalError("");
        setIsModalOpen(true);
    };

    // ----- Form change handlers -----

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setForm((f) => ({ ...f, [name]: checked }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    // ----- Save (create or update) -----

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setModalError("");

        try {
            // convert booleans to 0/1 for backend
            const payload = {
                ...form,
                audio_production: form.audio_production ? 1 : 0,
                show_notes: form.show_notes ? 1 : 0,
                distribution: form.distribution ? 1 : 0,
                hosting: form.hosting ? 1 : 0,
                short_form_video: form.short_form_video ? 1 : 0,
                long_form_video: form.long_form_video ? 1 : 0,
                blog_post_creation: form.blog_post_creation ? 1 : 0,
                podcast_website: form.podcast_website ? 1 : 0,
                email_marketing: form.email_marketing ? 1 : 0,
                episode_development: form.episode_development ? 1 : 0,
                real_time_producer: form.real_time_producer ? 1 : 0,
                guest_outreach: form.guest_outreach ? 1 : 0,
                paywall_content: form.paywall_content ? 1 : 0,
                client_id: form.client_id || null,
            };

            let res;
            if (form.id) {
                res = await updatePodcast(form.id, payload);
            } else {
                res = await createPodcast(payload);
            }

            if (!res.ok) {
                throw new Error(res.message || "Failed to save podcast");
            }

            // reload list
            const updated = await getPodcasts();
            if (updated.ok) setPodcasts(updated.podcasts);

            closeModal();
        } catch (err) {
            console.error(err);
            setModalError(err.message || "Failed to save podcast");
        } finally {
            setSaving(false);
        }
    };

    // ----- Delete -----

    const handleDelete = async () => {
        if (!form.id) return;
        if (!window.confirm("Delete this podcast?")) return;

        try {
            const res = await deletePodcast(form.id);
            if (!res.ok) {
                throw new Error(res.message || "Failed to delete podcast");
            }

            setPodcasts((prev) => prev.filter((x) => x.id !== form.id));
            closeModal();
        } catch (err) {
            console.error(err);
            setModalError(err.message || "Failed to delete podcast");
        }
    };

    // ----- Keyboard shortcuts (Esc + Ctrl/Cmd+S) -----

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isModalOpen) return;

            // ESC = request close with unsaved check
            if (e.key === "Escape") {
                e.preventDefault();
                requestCloseModal();
                return;
            }

            // Ctrl+S / Cmd+S = save
            const isSaveShortcut =
                (e.key === "s" || e.key === "S") &&
                (e.ctrlKey || e.metaKey);

            if (isSaveShortcut) {
                e.preventDefault();
                if (!saving && formRef.current) {
                    if (formRef.current.requestSubmit) {
                        formRef.current.requestSubmit();
                    } else {
                        formRef.current.submit();
                    }
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isModalOpen, saving, requestCloseModal, formRef, hasUnsavedChanges]);

    // ----- Filtered list -----

    const searchTerm = search.toLowerCase();
    const visiblePodcasts = podcasts.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const clientName = (p.client_name || "").toLowerCase();
        if (!searchTerm) return true;
        return name.includes(searchTerm) || clientName.includes(searchTerm);
    });

    const getClientNameById = (id) =>
        clients.find((c) => String(c.id) === String(id))?.name || "";

    const isEditing = !!form.id;

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className="portal-page">
                {/* Page Header */}
                <div className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">Podcasts</h2>
                        <p className="portal-page__subtitle">
                            View, search and manage podcast shows for your clients.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="btn btn--primary"
                        onClick={handleOpenCreate}
                    >
                        + Create Podcast
                    </button>
                </div>

                {/* Search Bar */}
                <div className="portal-filter-bar">
                    <div className="portal-filter-bar__group portal-filter-bar__group--grow">
                        <span className="portal-filter-bar__label">Search</span>
                        <input
                            type="text"
                            className="portal-filter-bar__search"
                            placeholder="Search by podcast or client…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {/* Page-level error */}
                {error && <div className="alert alert--error">{error}</div>}

                {/* List */}
                {loading && <p>Loading podcasts…</p>}

                {!loading && visiblePodcasts.length === 0 && !error && (
                    <p>No podcasts found.</p>
                )}

                {!loading && visiblePodcasts.length > 0 && (
                    <div className="episode-list">
                        {visiblePodcasts.map((p) => (
                            <div
                                key={p.id}
                                className="episode-card"
                                style={{ borderLeftColor: "#1d3557" }}
                                onClick={() => handleOpenEdit(p)}
                            >
                                <div className="episode-card__main">
                                    <h3 className="episode-card__title">
                                        {p.name}
                                    </h3>
                                    <div className="episode-card__podcast">
                                        {p.client_name || "No client assigned"}
                                    </div>
                                    <div className="episode-card__meta">
                                        {(p.frequency || "No frequency").trim()}
                                        {" • "}
                                        {p.monthly_cost != null
                                            ? `£${p.monthly_cost}`
                                            : "£0.00"}
                                        {p.publication_day
                                            ? ` • Publishes: ${p.publication_day}`
                                            : ""}
                                    </div>
                                </div>

                                <div className="episode-card__right">
                                    <span className="pill pill--role-client">
                                        Podcast
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* -------------- MODAL -------------- */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal modal--wide">
                        {/* Header */}
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">
                                    {isEditing ? "Edit Podcast" : "Create Podcast"}
                                </h3>
                                <p className="modal__subtitle">
                                    Manage podcast details, services and distribution settings.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="modal__close-btn"
                                onClick={requestCloseModal}
                            >
                                ✕
                            </button>
                        </div>

                        {modalError && (
                            <div className="alert alert--error">{modalError}</div>
                        )}

                        {/* Body */}
                        <form
                            className="modal__body"
                            onSubmit={handleSave}
                            ref={formRef}
                        >
                            <div className="modal__grid modal__grid--two-columns">
                                {/* Left Column */}
                                <div className="modal__column">
                                    {/* General */}
                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">
                                            General
                                        </legend>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Client
                                            </span>
                                            <select
                                                name="client_id"
                                                value={form.client_id}
                                                onChange={handleChange}
                                                required
                                                className="form-field__select"
                                            >
                                                <option value="">
                                                    Select client
                                                </option>
                                                {clients.map((c) => (
                                                    <option
                                                        key={c.id}
                                                        value={c.id}
                                                    >
                                                        {c.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Podcast Name
                                            </span>
                                            <input
                                                name="name"
                                                value={form.name}
                                                onChange={handleChange}
                                                required
                                                className="form-field__input"
                                            />
                                        </label>

                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "10px",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <label
                                                className="form-field"
                                                style={{ flex: "1 1 180px" }}
                                            >
                                                <span className="form-field__label">
                                                    Frequency
                                                </span>
                                                <input
                                                    name="frequency"
                                                    value={form.frequency}
                                                    onChange={handleChange}
                                                    placeholder="Weekly / Monthly / etc"
                                                    className="form-field__input"
                                                />
                                            </label>

                                            <label
                                                className="form-field"
                                                style={{ flex: "1 1 180px" }}
                                            >
                                                <span className="form-field__label">
                                                    Publication Day
                                                </span>
                                                <input
                                                    name="publication_day"
                                                    value={form.publication_day}
                                                    onChange={handleChange}
                                                    placeholder="e.g. Monday"
                                                    className="form-field__input"
                                                />
                                            </label>
                                        </div>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Monthly Cost (£)
                                            </span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="monthly_cost"
                                                value={form.monthly_cost}
                                                onChange={handleChange}
                                                className="form-field__input"
                                            />
                                        </label>
                                    </fieldset>

                                    {/* Services */}
                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">
                                            Services
                                        </legend>
                                        <div
                                            style={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "10px",
                                            }}
                                        >
                                            {[
                                                "audio_production",
                                                "show_notes",
                                                "distribution",
                                                "hosting",
                                                "short_form_video",
                                                "long_form_video",
                                                "blog_post_creation",
                                                "podcast_website",
                                                "email_marketing",
                                                "episode_development",
                                                "real_time_producer",
                                                "guest_outreach",
                                                "paywall_content",
                                            ].map((field) => (
                                                <label
                                                    key={field}
                                                    className="form-field__checkbox-row"
                                                    style={{
                                                        flex: "1 1 200px",
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name={field}
                                                        checked={form[field]}
                                                        onChange={
                                                            handleCheckboxChange
                                                        }
                                                    />
                                                    <span className="form-field__label">
                                                        {field
                                                            .replace(/_/g, " ")
                                                            .replace(
                                                                /\b\w/g,
                                                                (c) =>
                                                                    c.toUpperCase()
                                                            )}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </fieldset>
                                </div>

                                {/* Right Column */}
                                <div className="modal__column">
                                    {/* Distribution / Platform */}
                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">
                                            Distribution & Platform
                                        </legend>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Riverside
                                            </span>
                                            <input
                                                name="riverside"
                                                value={form.riverside}
                                                onChange={handleChange}
                                                placeholder="Yes / No / Link / Notes"
                                                className="form-field__input"
                                            />
                                        </label>

                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "10px",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <label
                                                className="form-field"
                                                style={{ flex: "1 1 180px" }}
                                            >
                                                <span className="form-field__label">
                                                    Riverside Username
                                                </span>
                                                <input
                                                    name="riverside_username"
                                                    value={
                                                        form.riverside_username
                                                    }
                                                    onChange={handleChange}
                                                    className="form-field__input"
                                                />
                                            </label>

                                            <label
                                                className="form-field"
                                                style={{ flex: "1 1 180px" }}
                                            >
                                                <span className="form-field__label">
                                                    Riverside Password
                                                </span>
                                                <input
                                                    name="riverside_password"
                                                    value={
                                                        form.riverside_password
                                                    }
                                                    onChange={handleChange}
                                                    className="form-field__input"
                                                />
                                            </label>
                                        </div>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Captivate Show ID
                                            </span>
                                            <input
                                                name="captivate_show_id"
                                                value={form.captivate_show_id}
                                                onChange={handleChange}
                                                className="form-field__input"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                SharePoint Folder Name
                                            </span>
                                            <input
                                                name="sharepoint_folder_name"
                                                value={
                                                    form.sharepoint_folder_name
                                                }
                                                onChange={handleChange}
                                                className="form-field__input"
                                            />
                                        </label>

                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "10px",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <label
                                                className="form-field"
                                                style={{ flex: "1 1 180px" }}
                                            >
                                                <span className="form-field__label">
                                                    Apple Podcasts URL
                                                </span>
                                                <input
                                                    name="apple_podcasts_url"
                                                    value={
                                                        form.apple_podcasts_url
                                                    }
                                                    onChange={handleChange}
                                                    className="form-field__input"
                                                />
                                            </label>

                                            <label
                                                className="form-field"
                                                style={{ flex: "1 1 180px" }}
                                            >
                                                <span className="form-field__label">
                                                    Spotify Podcasts URL
                                                </span>
                                                <input
                                                    name="spotify_podcasts_url"
                                                    value={
                                                        form.spotify_podcasts_url
                                                    }
                                                    onChange={handleChange}
                                                    className="form-field__input"
                                                />
                                            </label>
                                        </div>
                                    </fieldset>

                                    {/* Notes / Notifications */}
                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">
                                            Notes & Notifications
                                        </legend>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Notification Emails
                                            </span>
                                            <textarea
                                                name="notification_emails"
                                                value={form.notification_emails}
                                                onChange={handleChange}
                                                rows={2}
                                                className="form-field__textarea"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Notes
                                            </span>
                                            <textarea
                                                name="notes"
                                                value={form.notes}
                                                onChange={handleChange}
                                                rows={3}
                                                className="form-field__textarea"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Last Modified
                                            </span>
                                            <input
                                                name="modified_raw"
                                                value={form.modified_raw}
                                                onChange={handleChange}
                                                className="form-field__input"
                                                readOnly
                                            />
                                        </label>
                                    </fieldset>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="modal__footer">
                                {isEditing && (
                                    <button
                                        type="button"
                                        className="btn btn--danger"
                                        onClick={handleDelete}
                                    >
                                        Delete Podcast
                                    </button>
                                )}

                                <div style={{ display: "flex", gap: "10px" }}>
                                    <button
                                        type="button"
                                        className="btn btn--secondary"
                                        onClick={requestCloseModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="btn btn--primary"
                                    >
                                        {saving
                                            ? "Saving..."
                                            : isEditing
                                                ? "Save Changes"
                                                : "Create Podcast"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </PageShell>
    );
}

export default Podcasts;
